import type { TFunction } from 'i18next';
import type { WebhookDelivery } from '@services/webhookService';

type DeliveryErrorCode = NonNullable<WebhookDelivery['lastError']>;

/**
 * The text for each delivery error code (REV-1026). The API returns a code in
 * `lastError`, never text about the target; the words are ours. A Record, so a
 * code added to the contract fails the typecheck until it has a key here.
 */
const DELIVERY_ERROR_KEYS: Record<DeliveryErrorCode, string> = {
  target_private_network: 'webhooks.deliveryErrors.target_private_network',
  target_unresolvable: 'webhooks.deliveryErrors.target_unresolvable',
  target_invalid: 'webhooks.deliveryErrors.target_invalid',
  http_error: 'webhooks.deliveryErrors.http_error',
  timeout: 'webhooks.deliveryErrors.timeout',
  connection_failed: 'webhooks.deliveryErrors.connection_failed',
  payload_unavailable: 'webhooks.deliveryErrors.payload_unavailable',
  endpoint_inactive: 'webhooks.deliveryErrors.endpoint_inactive',
};

/**
 * Why a delivery failed, in the user's language, or null when it did not.
 * A code this build does not know (a newer API) gets the generic text.
 */
export function deliveryErrorText(
  t: TFunction,
  delivery: Pick<WebhookDelivery, 'lastError' | 'lastResponseCode'>
): string | null {
  const code = delivery.lastError;
  if (!code) return null;
  const key = DELIVERY_ERROR_KEYS[code] ?? 'webhooks.deliveryErrors.unknown';
  return t(key, { status: delivery.lastResponseCode ?? '' });
}
